/**
 * TWIST scenes ("here's why") for each clip-v2 format. The chart persists across
 * the scene's shots; each shot changes kicker, camera and overlays (a visual
 * change every <= 3 s).
 */
import React, { useMemo } from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { VIDEO_FONT_FAMILY } from "../../RegisterVideoFonts";
import { formatAmountOrUnlimited, formatEmphasis, formatMoney, formatNetDebitCredit, formatPrice, pnlAtPrice } from "../scenarioExplainerTimeline";
import { ClipChart, chartGeometry, computeChartDomain, morphSeries, resample } from "./ClipV2Chart";
import { Kicker, MOTION, Readout, Slab, clamp, countValue, ease, formatCount, lerp, popIn, sp, toneColor } from "./ClipV2Primitives";
import { SlamWords, bigNumberFont } from "./ClipV2Scenes";
import { assumptionLabel, compareVerdict, formatAssumptionValue, scenarioPriceLabel } from "./clipV2Timeline";

// ── Shared ──────────────────────────────────────────────────

function activeShot(shots, frame) {
  let index = 0;
  for (let i = 0; i < shots.length; i += 1) if (frame >= shots[i].from) index = i;
  const shot = shots[index];
  return { shot, f: frame - shot.from, lf: frame - shot.shotFrom, dur: shot.shotDurationInFrames };
}

/** Spring "impact" 1 → 0 starting at `at` (0 before). */
function impact(frame, fps, at) {
  if (at == null || frame < at) return 0;
  return 1 - sp(frame, fps, MOTION.punch, at);
}

/** Micro punch on every cut + alternate framing on split parts. */
function cutScale(shot, f, fps) {
  const base = shot.part % 2 === 1 ? 1.06 : 1;
  return base * (1 + 0.035 * (1 - sp(f, fps, MOTION.punch)));
}

function twistMetrics(layout) {
  const { s, L, stage, contentWidth } = layout;
  const gap = s(18);
  const kickerH = s(L.kickerFont * 1.9);
  const readH = Math.round(s(L.readoutLabelFont) * 1.4 + s(L.readoutValueFont) * 1.05);
  const legendH = s(L.chartFont * 1.7);
  const chartH = stage.height - kickerH - readH - legendH - gap * 3;
  return { gap, kickerH, readH, legendH, chartH, chartW: contentWidth };
}

function Legend({ items, colors, fontSize }) {
  return (
    <div style={{ display: "flex", gap: fontSize * 1.4, alignItems: "center", fontSize, fontWeight: 700, color: colors.muted, fontFamily: VIDEO_FONT_FAMILY }}>
      {items.map((item) => (
        <span key={item.label} style={{ display: "inline-flex", alignItems: "center", gap: fontSize * 0.45 }}>
          {item.dashed ? (
            <span style={{ width: fontSize * 1.6, borderTop: `${Math.max(3, fontSize * 0.14)}px dashed ${colors.muted}` }} />
          ) : (
            <span style={{ display: "inline-flex" }}>
              <span style={{ width: fontSize * 0.8, height: fontSize * 0.28, background: colors.negative, borderRadius: 3 }} />
              <span style={{ width: fontSize * 0.8, height: fontSize * 0.28, background: colors.accent, borderRadius: 3 }} />
            </span>
          )}
          {item.label}
        </span>
      ))}
    </div>
  );
}

function CauseChips({ chips, colors, fontSize, frame, fps, delay }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: fontSize * 0.45, justifyContent: "center" }}>
      {chips.map((chip, i) => (
        <div
          key={chip.text}
          style={{
            ...popIn(frame, fps, delay + i * 5, { from: 1.6, config: MOTION.slam }),
            fontSize,
            fontWeight: 900,
            letterSpacing: "-0.01em",
            transform: `${popIn(frame, fps, delay + i * 5, { from: 1.6 }).transform} rotate(${i % 2 ? 1.5 : -1.5}deg)`,
          }}
        >
          <Slab tone={chip.tone} colors={colors} fontSize={fontSize}>
            {chip.text}
          </Slab>
        </div>
      ))}
    </div>
  );
}

/** Kicker row + readouts row + chart (with camera) + legend, plus an optional overlay over the chart area. */
function TwistFrame({ layout, m, kicker, readouts, chart, chartTransform, chartOpacity = 1, legend, overlay }) {
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", gap: m.gap }}>
      <div style={{ height: m.kickerH, display: "flex", alignItems: "center" }}>{kicker}</div>
      <div style={{ height: m.readH, display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>{readouts}</div>
      <div style={{ position: "relative", height: m.chartH, overflow: "hidden", borderRadius: layout.s(layout.L.chartFont * 1.1) }}>
        <div style={{ position: "absolute", inset: 0, opacity: chartOpacity, ...chartTransform }}>{chart}</div>
        {overlay ? <div style={{ position: "absolute", inset: 0 }}>{overlay}</div> : null}
      </div>
      <div style={{ height: m.legendH, display: "flex", alignItems: "center", opacity: overlay ? 0 : 1 }}>{legend}</div>
    </AbsoluteFill>
  );
}

function BigOverlay({ value, format, label, chips, colors, layout, frame, fps, hitAt }) {
  const { s, L, contentWidth } = layout;
  const final = formatEmphasis({ value, format });
  const fs = bigNumberFont(final, s(L.bigNumberFont * 0.92), contentWidth);
  const shown = formatCount(countValue(frame, { from: 0, to: value, start: hitAt - 2, duration: 12 }), format, value);
  const punch = sp(frame, fps, MOTION.slam, hitAt - 2);
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: s(26), textAlign: "center" }}>
      <div
        style={{
          opacity: interpolate(frame, [hitAt - 2, hitAt + 1], [0, 1], clamp),
          transform: `scale(${lerp(1.8, 1, punch)})`,
        }}
      >
        <div style={{ fontSize: fs, fontWeight: 900, lineHeight: 0.92, letterSpacing: "-0.05em", color: toneColor(value, colors), fontVariantNumeric: "tabular-nums" }}>
          {shown}
        </div>
        <div style={{ fontSize: s(L.bigLabelFont), fontWeight: 800, color: colors.text, marginTop: s(14) }}>{label}</div>
      </div>
      {chips?.length ? <CauseChips chips={chips} colors={colors} fontSize={s(L.legsFont * 1.15)} frame={frame} fps={fps} delay={hitAt + 6} /> : null}
    </div>
  );
}

function pnlColor(value, colors) {
  if (Math.abs(value) < 0.5) return colors.text;
  return toneColor(value, colors);
}

// ── why-it-lost ─────────────────────────────────────────────

function WhyItLostTwist({ clip, layout, frame, fps, shots }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const m = twistMetrics(layout);
  const { fromSpot, toSpot, days, ivChangePct } = clip.scenarioMove;
  const main = clip.payoff.tPlus0 ?? clip.payoff.expiration;
  const ghost = clip.payoff.tPlus0 ? clip.payoff.expiration : null;
  const be = [...clip.numbers.breakevens].sort((a, b) => Math.abs(a - toSpot) - Math.abs(b - toSpot))[0];
  const domain = useMemo(() => computeChartDomain([main, ghost], [fromSpot, toSpot, be]), [main, ghost, fromSpot, toSpot, be]);
  const { shot, f, lf, dur } = activeShot(shots, frame);
  const ti = shot.twistIndex;

  const reveal = ti === 0 ? ease(lf, 3, Math.round(dur * 0.7), Easing.inOut(Easing.cubic)) : 1;
  const tMove = ti === 0 ? 0 : ti === 1 ? ease(lf, 5, Math.round(dur * 0.62), Easing.inOut(Easing.cubic)) : 1;
  const price = lerp(fromSpot, toSpot, tMove);
  const pnl = lerp(0, clip.pnlAtMove, tMove);
  const hitLocal = shot.hitFrame != null ? shot.hitFrame - shot.shotFrom : null;
  const hit = ti === 1 ? impact(lf, fps, hitLocal) : 0;

  const g = chartGeometry(domain, m.chartW, m.chartH, s(L.chartFont));
  const origin = `${g.x(toSpot)}px ${g.y(clip.pnlAtMove)}px`;
  let zoom = 1;
  let chartOpacity = 1;
  if (ti === 2) {
    const p = sp(lf, fps, MOTION.punch);
    zoom = lerp(1, 1.35, p);
    chartOpacity = lerp(1, 0.2, p);
  } else if (ti === 3) {
    const p = sp(lf, fps, MOTION.punch);
    zoom = lerp(1.35, 1, p);
    chartOpacity = lerp(0.2, 1, p);
  }
  const scale = zoom * cutScale(shot, f, fps);

  const kickers = ["Here's why", "The move", "The catch", "The bar"];
  const moveAbs = Math.abs(toSpot - fromSpot);
  const movePct = ((toSpot - fromSpot) / fromSpot) * 100;
  const chips = [
    { text: `Stock ${toSpot > fromSpot ? "+" : "−"}${formatPrice(moveAbs)}`, tone: "gain" },
    ivChangePct != null && ivChangePct !== 0
      ? { text: `IV ${ivChangePct > 0 ? "+" : "−"}${Math.abs(ivChangePct)} pts`, tone: ivChangePct < 0 ? "loss" : "gain" }
      : null,
    days > 0 ? { text: `${days} day${days === 1 ? "" : "s"} of decay`, tone: "loss" } : null,
  ].filter(Boolean);

  const readFonts = { labelFont: s(L.readoutLabelFont), valueFont: s(L.readoutValueFont) };
  const beOpacity = ti === 3 ? ease(lf, 8, 10) : 0;
  const readouts =
    ti === 3 ? (
      <>
        <Readout label={scenarioPriceLabel(clip)} value={formatPrice(toSpot)} color={colors.text} colors={colors} {...readFonts} />
        <div style={{ opacity: beOpacity }}>
          <Readout label="Breakeven at expiry" value={formatPrice(be)} color={colors.accent} colors={colors} align="right" {...readFonts} />
        </div>
      </>
    ) : (
      <>
        <div style={{ display: "flex", alignItems: "flex-end", gap: s(16) }}>
          <Readout label={`${clip.ticker} price`} value={formatPrice(price)} color={colors.text} colors={colors} {...readFonts} />
          {ti >= 1 ? (
            <div
              style={{
                ...popIn(lf, fps, Math.round(dur * 0.55), { from: 1.8 }),
                fontSize: s(L.readoutLabelFont * 1.25),
                fontWeight: 900,
                marginBottom: s(8),
              }}
            >
              <Slab colors={colors} fontSize={s(L.readoutLabelFont * 1.25)}>
                {`${movePct >= 0 ? "+" : "−"}${Math.abs(movePct).toFixed(1)}%`}
              </Slab>
            </div>
          ) : null}
        </div>
        <div style={{ transform: `scale(${1 + hit * 0.3})`, transformOrigin: "right bottom" }}>
          <Readout
            label={ti === 0 ? "P&L at entry" : "Modeled P&L"}
            value={formatCount(pnl, "signedCurrency", clip.pnlAtMove)}
            color={pnlColor(pnl, colors)}
            colors={colors}
            align="right"
            {...readFonts}
          />
        </div>
      </>
    );

  return (
    <TwistFrame
      layout={layout}
      m={m}
      kicker={<Kicker key={`k-${ti}`} text={kickers[ti] ?? "Here's why"} colors={colors} fontSize={s(L.kickerFont)} frame={lf} fps={fps} tone={ti === 2 ? "loss" : "gain"} />}
      readouts={ti === 2 ? null : readouts}
      chartOpacity={chartOpacity}
      chartTransform={{ transform: `scale(${scale})`, transformOrigin: origin }}
      chart={
        <ClipChart
          idPrefix="wil"
          width={m.chartW}
          height={m.chartH}
          domain={domain}
          colors={colors}
          fontSize={s(L.chartFont)}
          curves={[
            ...(ghost ? [{ points: ghost, style: "ghost", reveal }] : []),
            { points: main, style: "signed", reveal },
          ]}
          vLines={[{ price: be, label: `Breakeven ${formatPrice(be)}`, color: colors.accent, opacity: beOpacity, labelY: "bottom" }]}
          marker={{
            price,
            pnl: pnlAtPrice(main, price),
            dot: ti >= 1,
            opacity: ti === 0 ? ease(lf, Math.round(dur * 0.45), 8) : 1,
            pill: `${clip.ticker} ${formatPrice(price)}`,
            pulse: 1 + hit * 0.6,
          }}
        />
      }
      overlay={
        ti === 2 ? (
          <BigOverlay
            value={clip.pnlAtMove}
            format="signedCurrency"
            label={days > 0 ? `Modeled P&L after ${days} day${days === 1 ? "" : "s"}` : "Modeled P&L"}
            chips={chips}
            colors={colors}
            layout={layout}
            frame={lf}
            fps={fps}
            hitAt={hitLocal ?? 4}
          />
        ) : null
      }
      legend={
        <Legend
          colors={colors}
          fontSize={s(L.chartFont * 0.95)}
          items={[
            { label: ghost ? (days > 0 ? `After ${days} day${days === 1 ? "" : "s"} (modeled)` : "Today (modeled)") : "At expiration" },
            ...(ghost ? [{ label: "At expiration", dashed: true }] : []),
          ]}
        />
      }
    />
  );
}

// ── compare-two ─────────────────────────────────────────────

function lossValue(strategy) {
  return strategy.numbers.maxLoss === "Unlimited" ? null : -Math.abs(strategy.numbers.maxLoss);
}

function ComparePanel({ strategy, index, layout, domain, width, height, frame, fps, reveal, lossT, risky, safer, delay }) {
  const { s, L } = layout;
  const { colors } = strategy.__brand;
  const vertical = layout.key === "vertical";
  const headerH = vertical ? s(84) : s(118);
  const loss = lossValue(strategy);
  const lossShown = loss == null ? "Unlimited" : formatCount(lossT > 0.995 ? loss : lerp(0, loss, lossT), "signedCurrency", loss);
  const accent = index === 0 ? colors.accent : colors.primary;
  const border = risky && lossT > 0 ? colors.negative : safer && lossT > 0 ? accent : "transparent";
  return (
    <div
      style={{
        ...popIn(frame, fps, delay, { from: 0.85, rise: s(30), config: MOTION.settle }),
        width,
        height,
        boxSizing: "border-box",
        borderRadius: s(28),
        background: colors.surface,
        border: `${s(5)}px solid ${border}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: headerH,
          padding: `0 ${s(22)}px`,
          display: "flex",
          flexDirection: vertical ? "row" : "column",
          justifyContent: vertical ? "space-between" : "center",
          alignItems: vertical ? "center" : "flex-start",
          gap: vertical ? 0 : s(4),
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: s(12) }}>
          <span
            style={{
              width: s(44),
              height: s(44),
              borderRadius: "50%",
              background: accent,
              color: index === 0 ? colors.background : "#FFFFFF",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: s(26),
              fontWeight: 900,
            }}
          >
            {index === 0 ? "A" : "B"}
          </span>
          <span style={{ fontSize: s(L.cardBodyFont * 1.15), fontWeight: 900, color: colors.text }}>{strategy.label}</span>
        </div>
        <div style={{ opacity: lossT > 0 ? 1 : 0, fontSize: s(L.cardBodyFont * 1.15), fontWeight: 900, color: colors.negative, fontVariantNumeric: "tabular-nums" }}>
          {`Max loss ${lossShown}`}
        </div>
      </div>
      <ClipChart
        idPrefix={`cmp-${index}`}
        width={width - s(10)}
        height={height - headerH - s(10)}
        domain={domain}
        colors={colors}
        fontSize={s(L.chartFont * 0.85)}
        background={false}
        curves={[{ points: strategy.payoff.expiration, style: "signed", reveal }]}
        hLines={loss == null ? [] : [{ pnl: loss, color: colors.negative, opacity: lossT > 0 ? 1 : 0 }]}
      />
    </div>
  );
}

function CompareTable({ clip, layout, frame, fps, verdict }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const fs = s(L.tableFont);
  const [a, b] = clip.strategies;
  const rows = [
    ["Cost", (x) => formatNetDebitCredit(x.numbers.netDebitCredit).value],
    ["Max loss", (x) => formatAmountOrUnlimited(x.numbers.maxLoss, { loss: true }), "loss"],
    ["Max gain", (x) => formatAmountOrUnlimited(x.numbers.maxGain)],
    [a.numbers.breakevens.length > 1 || b.numbers.breakevens.length > 1 ? "Breakevens" : "Breakeven", (x) => x.numbers.breakevens.map(formatPrice).join(" / ")],
  ];
  const cell = { flex: 1, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: s(14), height: "100%", justifyContent: "center" }}>
      <div style={{ display: "flex", fontSize: fs * 0.8, fontWeight: 900, color: colors.muted, textTransform: "uppercase", letterSpacing: "0.06em", padding: `0 ${s(26)}px`, ...popIn(frame, fps, 0, { from: 0.9 }) }}>
        <div style={{ flex: 1.1 }} />
        <div style={{ ...cell, color: colors.accent }}>{a.label}</div>
        <div style={{ ...cell, color: "#FFFFFF" }}>{b.label}</div>
      </div>
      {rows.map(([label, get, kind], i) => (
        <div
          key={label}
          style={{
            ...popIn(frame, fps, 3 + i * 4, { from: 0.9, rise: s(30), config: MOTION.settle }),
            display: "flex",
            alignItems: "center",
            padding: `${s(24)}px ${s(26)}px`,
            borderRadius: s(22),
            background: colors.surface,
            fontSize: fs,
            fontWeight: 900,
            color: colors.text,
          }}
        >
          <div style={{ flex: 1.1, color: colors.muted, fontSize: fs * 0.8, fontWeight: 800 }}>{label}</div>
          {[a, b].map((x, xi) => (
            <div key={xi} style={cell}>
              {kind === "loss" && xi === verdict.saferIndex ? (
                <Slab colors={colors} fontSize={fs} progress={sp(frame, fps, MOTION.settle, 3 + i * 4 + 8)}>
                  {get(x)}
                </Slab>
              ) : (
                <span style={{ color: kind === "loss" ? colors.negative : colors.text }}>{get(x)}</span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function CompareTwoTwist({ clip, layout, frame, fps, shots }) {
  const { s, L, contentWidth } = layout;
  const { colors } = clip.brand;
  const verdict = compareVerdict(clip);
  const [a, b] = clip.strategies;
  const domain = useMemo(
    () =>
      computeChartDomain(
        [a.payoff.expiration, b.payoff.expiration],
        [clip.spot * 0.93, clip.spot * 1.07, ...a.numbers.breakevens, ...b.numbers.breakevens],
        { capY: [a, b].map(lossValue).filter((v) => v != null) }
      ),
    [a, b, clip.spot]
  );
  const { shot, f, lf, dur } = activeShot(shots, frame);
  const ti = shot.twistIndex;
  const gap = s(20);
  const kickerH = s(L.kickerFont * 1.9);
  const bodyH = layout.stage.height - kickerH - gap;
  const vertical = layout.key === "vertical";
  const panelW = vertical ? contentWidth : Math.floor((contentWidth - gap) / 2);
  const panelH = vertical ? Math.floor((bodyH - gap) / 2) : bodyH;
  const reveal = ti === 0 ? ease(lf, 6, Math.round(dur * 0.6), Easing.inOut(Easing.cubic)) : 1;
  const hitLocal = shot.hitFrame != null ? shot.hitFrame - shot.shotFrom : 12;
  const lossT = ti === 0 ? 0 : ti === 1 ? ease(lf, hitLocal - 10, 14, Easing.out(Easing.exp)) : 1;
  const kickers = ["Same view, 2 trades", "The downside", "Side by side", "Verdict"];
  const brandify = (x) => ({ ...x, __brand: clip.brand });
  const scale = cutScale(shot, f, fps);

  let body;
  if (ti <= 1) {
    body = (
      <div style={{ display: "flex", flexDirection: vertical ? "column" : "row", gap, transform: `scale(${scale})` }}>
        {[a, b].map((x, i) => (
          <ComparePanel
            key={x.label}
            strategy={brandify(x)}
            index={i}
            layout={layout}
            domain={domain}
            width={panelW}
            height={panelH}
            frame={ti === 0 ? lf : 99}
            fps={fps}
            reveal={reveal}
            lossT={lossT}
            risky={i !== verdict.saferIndex}
            safer={i === verdict.saferIndex}
            delay={i * 6}
          />
        ))}
      </div>
    );
  } else if (ti === 2) {
    body = (
      <div style={{ height: bodyH, transform: `scale(${scale})` }}>
        <CompareTable clip={clip} layout={layout} frame={lf} fps={fps} verdict={verdict} />
      </div>
    );
  } else {
    body = (
      <div style={{ height: bodyH, display: "flex", flexDirection: "column", justifyContent: "center", gap: s(44), transform: `scale(${scale})` }}>
        <SlamWords
          text={verdict.text}
          frame={lf}
          fps={fps}
          style={{ fontSize: s(L.lessonFont), fontWeight: 900, lineHeight: 1.06, letterSpacing: "-0.035em", color: colors.text }}
        />
        <div style={{ display: "flex", gap }}>
          {[a, b].map((x, i) => {
            const safer = i === verdict.saferIndex;
            return (
              <div
                key={x.label}
                style={{
                  ...popIn(lf, fps, 8 + i * 4, { from: safer ? 1.3 : 0.9, config: MOTION.bounce }),
                  flex: 1,
                  padding: `${s(26)}px ${s(26)}px`,
                  borderRadius: s(26),
                  background: safer ? colors.primary : colors.surface,
                  opacity: safer ? 1 : 0.55,
                }}
              >
                <div style={{ fontSize: s(L.readoutLabelFont), fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: safer ? "#FFFFFF" : colors.muted }}>
                  {`${x.label} max loss`}
                </div>
                <div style={{ fontSize: s(L.readoutValueFont * 0.85), fontWeight: 900, color: safer ? "#FFFFFF" : colors.negative, marginTop: s(8) }}>
                  {formatAmountOrUnlimited(x.numbers.maxLoss, { loss: true })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", gap }}>
      <div style={{ height: kickerH, display: "flex", alignItems: "center" }}>
        <Kicker key={`k-${ti}`} text={kickers[ti]} colors={colors} fontSize={s(L.kickerFont)} frame={lf} fps={fps} tone={ti === 1 ? "loss" : "gain"} />
      </div>
      {body}
    </AbsoluteFill>
  );
}

// ── change-one ──────────────────────────────────────────────

function ChangeOneTwist({ clip, layout, frame, fps, shots }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const m = twistMetrics(layout);
  const { assumption, baseline, changed } = clip;
  const isPrice = assumption.kind === "price";
  const be = clip.numbers.breakevens[0];
  const domain = useMemo(
    () =>
      computeChartDomain(
        [baseline.payoff, changed.payoff],
        isPrice ? [assumption.from, assumption.to, be] : [clip.spot, be],
        { extraPnls: [baseline.pnl, changed.pnl] }
      ),
    [baseline, changed, isPrice, assumption, be, clip.spot]
  );
  const base = useMemo(() => resample(baseline.payoff, domain), [baseline, domain]);
  const chg = useMemo(() => resample(changed.payoff, domain), [changed, domain]);
  const { shot, f, lf, dur } = activeShot(shots, frame);
  const ti = shot.twistIndex;
  const t = ti === 0 ? 0 : ti === 1 ? ease(lf, 6, Math.round(dur * 0.72), Easing.inOut(Easing.cubic)) : 1;
  const curve = morphSeries(base, chg, t);
  const reveal = ti === 0 ? ease(lf, 3, Math.round(dur * 0.65), Easing.inOut(Easing.cubic)) : 1;
  const price = isPrice ? lerp(assumption.from, assumption.to, t) : clip.spot;
  const pnl = lerp(baseline.pnl, changed.pnl, t);
  const hitLocal = shot.hitFrame != null ? shot.hitFrame - shot.shotFrom : null;
  const hit = ti === 1 ? impact(lf, fps, hitLocal) : 0;
  const rawValue = lerp(assumption.from, assumption.to, t);
  const bothInt = Number.isInteger(assumption.from) && Number.isInteger(assumption.to);
  const dialValue = formatAssumptionValue(assumption.kind, assumption.kind === "price" ? rawValue : bothInt ? Math.round(rawValue) : Math.round(rawValue * 10) / 10);

  const g = chartGeometry(domain, m.chartW, m.chartH, s(L.chartFont));
  const origin = `${g.x(isPrice ? assumption.to : clip.spot)}px ${g.y(changed.pnl)}px`;
  let zoom = 1;
  let chartOpacity = 1;
  if (ti === 2) {
    const p = sp(lf, fps, MOTION.punch);
    zoom = lerp(1, 1.35, p);
    chartOpacity = lerp(1, 0.2, p);
  }
  const scale = zoom * cutScale(shot, f, fps);
  const readFonts = { labelFont: s(L.readoutLabelFont), valueFont: s(L.readoutValueFont) };
  const kickers = ["Before", "Change one thing", "After"];
  const delta = changed.pnl - baseline.pnl;

  return (
    <TwistFrame
      layout={layout}
      m={m}
      kicker={<Kicker key={`k-${ti}`} text={kickers[ti] ?? "After"} colors={colors} fontSize={s(L.kickerFont)} frame={lf} fps={fps} tone={ti === 2 && delta < 0 ? "loss" : "gain"} />}
      readouts={
        ti === 2 ? null : (
          <>
            <div style={{ transform: `scale(${ti === 1 ? 1 + 0.05 * Math.sin(Math.PI * t) : 1})`, transformOrigin: "left bottom" }}>
              <Readout label={assumptionLabel(assumption)} value={dialValue} color={ti === 1 ? colors.accent : colors.text} colors={colors} {...readFonts} />
            </div>
            <div style={{ transform: `scale(${1 + hit * 0.3})`, transformOrigin: "right bottom" }}>
              <Readout label="Modeled P&L" value={formatCount(pnl, "signedCurrency", changed.pnl)} color={pnlColor(pnl, colors)} colors={colors} align="right" {...readFonts} />
            </div>
          </>
        )
      }
      chartOpacity={chartOpacity}
      chartTransform={{ transform: `scale(${scale})`, transformOrigin: origin }}
      chart={
        <ClipChart
          idPrefix="chg"
          width={m.chartW}
          height={m.chartH}
          domain={domain}
          colors={colors}
          fontSize={s(L.chartFont)}
          curves={[
            ...(ti >= 1 ? [{ points: base, style: "ghost", reveal: 1, opacity: ease(lf, 0, 6) * (ti >= 1 ? 1 : 0) }] : []),
            { points: curve, style: "signed", reveal },
          ]}
          marker={{
            price,
            pnl: pnlAtPrice(curve, price),
            opacity: ti === 0 ? ease(lf, Math.round(dur * 0.45), 8) : 1,
            pill: `${clip.ticker} ${formatPrice(price)}`,
            pulse: 1 + hit * 0.6,
          }}
        />
      }
      overlay={
        ti === 2 ? (
          <BigOverlay
            value={delta}
            format="signedCurrency"
            label="Modeled P&L change"
            chips={[
              { text: `Before ${formatMoney(Math.round(baseline.pnl), { signed: true })}`, tone: "gain" },
              { text: `After ${formatMoney(Math.round(changed.pnl), { signed: true })}`, tone: changed.pnl < baseline.pnl ? "loss" : "gain" },
            ]}
            colors={colors}
            layout={layout}
            frame={lf}
            fps={fps}
            hitAt={hitLocal ?? 4}
          />
        ) : null
      }
      legend={
        <Legend
          colors={colors}
          fontSize={s(L.chartFont * 0.95)}
          items={[
            { label: `${formatAssumptionValue(assumption.kind, ti === 0 ? assumption.from : assumption.to)} · modeled` },
            ...(ti >= 1 ? [{ label: `Before: ${formatAssumptionValue(assumption.kind, assumption.from)}`, dashed: true }] : []),
          ]}
        />
      }
    />
  );
}

// ── explainer ───────────────────────────────────────────────

function ExplainerTwist({ clip, layout, frame, fps, shots }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const m = twistMetrics(layout);
  const main = clip.payoff.expiration;
  const ghost = clip.payoff.tPlus0;
  const markerPrices = clip.beats.map((b) => b.markerPrice).filter(Boolean);
  const domain = useMemo(
    () => computeChartDomain([main, ghost], [clip.spot, ...markerPrices, ...clip.numbers.breakevens]),
    [main, ghost, clip.spot, markerPrices.join(","), clip.numbers.breakevens]
  );
  const { shot, f, lf, dur } = activeShot(shots, frame);
  const ti = shot.twistIndex;
  const beatIndex = shot.beatIndex;
  const reveal = ti === 0 ? ease(lf, 3, Math.round(dur * 0.7), Easing.inOut(Easing.cubic)) : 1;

  // Marker keyframes: spot, then each beat's markerPrice (or the previous price).
  const prices = [clip.spot];
  clip.beats.forEach((b) => prices.push(b.markerPrice ?? prices[prices.length - 1]));
  const prev = beatIndex == null ? clip.spot : prices[beatIndex];
  const target = beatIndex == null ? clip.spot : prices[beatIndex + 1];
  const price = lerp(prev, target, ease(lf, 2, 20, Easing.inOut(Easing.cubic)));
  const beat = beatIndex == null ? null : clip.beats[beatIndex];
  const hitLocal = shot.hitFrame != null ? shot.hitFrame - shot.shotFrom : 8;
  const hit = beat?.emphasis ? impact(lf, fps, hitLocal) : 0;

  const g = chartGeometry(domain, m.chartW, m.chartH, s(L.chartFont));
  const punchTarget = beatIndex != null && beatIndex % 2 === 1 ? 1.07 : 1;
  const zoom = lerp(1, punchTarget, sp(lf, fps, MOTION.punch));
  const scale = zoom * cutScale(shot, f, fps);
  // Anchor the punch-in on the price pill line so the pill stays in frame.
  const origin = `${g.x(target)}px ${g.padT - s(L.chartFont) * 1.35}px`;
  const readFonts = { labelFont: s(L.readoutLabelFont), valueFont: s(L.readoutValueFont) };

  let right;
  if (!beat) {
    right = (
      <Readout label="Max loss" value={formatAmountOrUnlimited(clip.numbers.maxLoss, { loss: true })} color={colors.negative} colors={colors} align="right" {...readFonts} />
    );
  } else if (beat.emphasis) {
    const e = beat.emphasis;
    const shown = formatCount(countValue(lf, { from: 0, to: e.value, start: hitLocal - 6, duration: 12 }), e.format, e.value);
    right = (
      <div style={{ transform: `scale(${1 + hit * 0.3})`, transformOrigin: "right bottom" }}>
        <Readout label={e.label || "Key number"} value={shown} color={e.format === "price" ? colors.accent : pnlColor(e.value, colors)} colors={colors} align="right" {...readFonts} />
      </div>
    );
  } else {
    const p = pnlAtPrice(main, price);
    right = <Readout label="P&L at expiry" value={formatMoney(Math.round(p), { signed: true })} color={pnlColor(p, colors)} colors={colors} align="right" {...readFonts} />;
  }

  const kicker = beat ? `Point ${beatIndex + 1} of ${clip.beats.length}` : "The payoff";

  return (
    <TwistFrame
      layout={layout}
      m={m}
      kicker={<Kicker key={`k-${ti}`} text={kicker} colors={colors} fontSize={s(L.kickerFont)} frame={lf} fps={fps} />}
      readouts={
        <>
          <Readout label={`${clip.ticker} price`} value={formatPrice(price)} color={colors.text} colors={colors} {...readFonts} />
          {right}
        </>
      }
      chartTransform={{ transform: `scale(${scale})`, transformOrigin: origin }}
      chart={
        <ClipChart
          idPrefix="exp"
          width={m.chartW}
          height={m.chartH}
          domain={domain}
          colors={colors}
          fontSize={s(L.chartFont)}
          curves={[...(ghost ? [{ points: ghost, style: "ghost", reveal }] : []), { points: main, style: "signed", reveal }]}
          vLines={clip.numbers.breakevens.map((be) => ({ price: be, label: `Breakeven ${formatPrice(be)}`, color: colors.accent, opacity: ti === 0 ? ease(lf, Math.round(dur * 0.6), 8) : 1, labelY: "bottom" }))}
          hLines={
            clip.numbers.maxLoss === "Unlimited"
              ? []
              : [{ pnl: -Math.abs(clip.numbers.maxLoss), color: colors.negative, opacity: ti === 0 ? ease(lf, Math.round(dur * 0.6), 8) : 1 }]
          }
          marker={{
            price,
            pnl: pnlAtPrice(main, price),
            opacity: ti === 0 ? ease(lf, Math.round(dur * 0.5), 8) : 1,
            pill: `${clip.ticker} ${formatPrice(price)}`,
            pulse: 1 + hit * 0.6,
          }}
        />
      }
      legend={<Legend colors={colors} fontSize={s(L.chartFont * 0.95)} items={[{ label: "At expiration" }, ...(ghost ? [{ label: "Today (modeled)", dashed: true }] : [])]} />}
    />
  );
}

export function TwistScene(props) {
  switch (props.clip.format) {
    case "why-it-lost":
      return <WhyItLostTwist {...props} />;
    case "compare-two":
      return <CompareTwoTwist {...props} />;
    case "change-one":
      return <ChangeOneTwist {...props} />;
    default:
      return <ExplainerTwist {...props} />;
  }
}
